import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Paper,
} from "@mui/material";
import React, { memo } from "react";

/**
 * NavigationBar component for PDF Table Viewer
 * Contains essential navigation controls: Table selector, Show Stats, and Hide PDF
 */
const NavigationBar = ({
  // Table Selector
  tables,
  selectedTable,
  handleTableChange,

  // Toggles
  showStats,
  setShowStats,

  // PDF Panel Toggle
  pdfPanelExpanded,
  setPdfPanelExpanded,
}) => {
  // Helper function to format pages display
  const formatPagesDisplay = (pages) => {
    if (!pages || pages.length === 0) return "N/A";
    if (pages.length <= 8) return pages.join(", ");

    const firstFive = pages.slice(0, 5).join(", ");
    const lastThree = pages.slice(-3).join(", ");
    return `${firstFive}, ..., ${lastThree}`;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: "absolute",
        top: 6,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        px: { xs: 2, sm: 2.5, md: 3 },
        py: { xs: 0.5, sm: 0.75 },
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: 0.75,
        backdropFilter: "blur(8px)",
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        width: "fit-content",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        mb: 1,
        maxWidth: "calc(100vw - 48px)",
        overflowX: "auto",
        overflowY: "hidden",
        "&::-webkit-scrollbar": {
          height: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(0, 0, 0, 0.05)",
          borderRadius: "2px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          borderRadius: "2px",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          },
        },
      }}
    >
      {/* Table Selector */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.5, sm: 0.75 },
          flex: "0 0 auto",
        }}
      >
        <FormControl
          size={"small"}
          sx={{
            minWidth: { xs: 180, sm: 200 },
            "& .MuiOutlinedInput-root": {
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              height: { xs: "36px", sm: "40px" },
            },
          }}
        >
          <InputLabel
            id={"table-select-label"}
            sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
          >
            {"Select Table"}
          </InputLabel>
          <Select
            labelId={"table-select-label"}
            value={selectedTable?.table_id || selectedTable?.id || ""}
            label={"Select Table"}
            onChange={handleTableChange}
            sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
          >
            {tables.map((table, index) => {
              // Handle separator
              if (table.isSeparator) {
                return (
                  <MenuItem
                    key={`separator-${index}`}
                    disabled
                    sx={{
                      opacity: 0.6,
                      fontSize: { xs: "0.65rem", sm: "0.7rem" },
                    }}
                  >
                    {"─────── Individual Tables ───────"}
                  </MenuItem>
                );
              }

              // Calculate proper index for individual tables (after separator)
              const separatorIndex = tables.findIndex((t) => t.isSeparator);
              const isIndividualTable =
                separatorIndex >= 0 && index > separatorIndex;
              const displayIndex = isIndividualTable
                ? index - separatorIndex
                : index + 1;

              // Determine table name
              const tableName =
                !isIndividualTable && displayIndex === 1
                  ? "Combined Table"
                  : `Table ${displayIndex}`;

              return (
                <MenuItem
                  key={table.table_id || table.id || index}
                  value={table.table_id || table.id}
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant={"body2"}
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                    >
                      {`${tableName} (${table.rows?.length || 0} rows)`}
                    </Typography>
                    <Typography
                      variant={"caption"}
                      color={"text.secondary"}
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                      }}
                    >
                      {`Page${
                        table.pages?.length > 1 ? "s" : ""
                      } ${formatPagesDisplay(table.pages)}`}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "1px",
          height: "28px",
          backgroundColor: "rgba(0, 0, 0, 0.15)",
        }}
      />

      {/* PDF Panel Toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.75, sm: 1 },
          flex: "0 0 auto",
        }}
      >
        <IconButton
          onClick={() => setPdfPanelExpanded(!pdfPanelExpanded)}
          size={"small"}
          color={pdfPanelExpanded ? "primary" : "default"}
          sx={{ p: 0.75 }}
        >
          {pdfPanelExpanded ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
        <Typography
          variant={"body2"}
          sx={{
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
            whiteSpace: "nowrap",
            fontWeight: 500,
            display: "block",
          }}
        >
          {pdfPanelExpanded ? "Hide PDF" : "Show PDF"}
        </Typography>
      </Box>
    </Paper>
  );
};

export default memo(NavigationBar);
